import React from 'react';
import SEOHead from '../components/SEOHead';
import ContactForm from '../components/ContactForm';
import { COMPANY_INFO, SEO_CONFIG } from '../data/companyData';

export default function ContactPage() {
  return (
    <>
      <SEOHead
        title={SEO_CONFIG.contact.title}
        description={SEO_CONFIG.contact.description}
      />

      {/* Hero Header */}
      <section className="bg-neutral-950 text-white py-16 sm:py-20 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-neutral-900 border border-neutral-800 text-[#DFBF77] text-xs font-semibold uppercase tracking-wider shadow-xs">
                <i className="fa-solid fa-headset" aria-hidden="true" />
                Kontakt os
              </div>
              <h1 className="mt-4 pt-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
                Få rådgivning eller et <span className="text-[#DFBF77]">uforpligtende tilbud</span>
              </h1>
            </div>
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
              Uanset om du planlægger et nyt anlæg, ønsker at energioptimere dine nuværende køleinstallationer eller har brug for teknisk rådgivning, er vi klar til at hjælpe.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section: Info + Form */}
      <section className="py-16 sm:py-20 bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Contact Information */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <span className="inline-block text-xs font-bold text-[#8D6D2F] uppercase tracking-wider bg-[#FAF6ED] px-3.5 py-1.5 rounded-full border border-[#EAD5A5] shadow-xs">
                  Virksomhedsoplysninger
                </span>
                <h2 className="mt-3.5 pt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-neutral-950">
                  {COMPANY_INFO.name}
                </h2>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
                  Vi holder til i Hillerød og servicerer kunder i hele Nordsjælland og Storkøbenhavn.
                </p>
              </div>

              {/* Direct Info Cards */}
              <div className="space-y-4">
                {/* Phone */}
                <div className="p-5 rounded-2xl bg-white border border-neutral-200 shadow-xs flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FAF6ED] border border-[#EAD5A5] text-[#8D6D2F] flex items-center justify-center text-base flex-shrink-0">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold uppercase tracking-wider text-neutral-500">Telefon</span>
                    <a
                      href={COMPANY_INFO.phoneHref}
                      className="text-base font-bold text-neutral-900 hover:text-[#B08B3E] transition-colors whitespace-nowrap"
                    >
                      {COMPANY_INFO.phoneFormatted}
                    </a>
                    <span className="text-[11px] text-neutral-500">Direkte teknisk kontakt</span>
                  </div>
                </div>

                {/* Email */}
                <div className="p-5 rounded-2xl bg-white border border-neutral-200 shadow-xs flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FAF6ED] border border-[#EAD5A5] text-[#8D6D2F] flex items-center justify-center text-base flex-shrink-0">
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold uppercase tracking-wider text-neutral-500">Email</span>
                    <a
                      href={COMPANY_INFO.emailHref}
                      className="text-base font-bold text-neutral-900 hover:text-[#B08B3E] transition-colors whitespace-nowrap"
                    >
                      {COMPANY_INFO.email}
                    </a>
                    <span className="text-[11px] text-neutral-500">Vi besvarer hurtigt alle henvendelser</span>
                  </div>
                </div>

                {/* Address */}
                <div className="p-5 rounded-2xl bg-white border border-neutral-200 shadow-xs flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FAF6ED] border border-[#EAD5A5] text-[#8D6D2F] flex items-center justify-center text-base flex-shrink-0">
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold uppercase tracking-wider text-neutral-500">Adresse</span>
                    <span className="text-base font-bold text-neutral-900">
                      {COMPANY_INFO.address.street}
                    </span>
                    <span className="text-sm text-neutral-600">
                      {COMPANY_INFO.address.zip} {COMPANY_INFO.address.city}
                    </span>
                  </div>
                </div>

                {/* CVR & Opening Hours */}
                <div className="p-5 rounded-2xl bg-neutral-950 text-white border border-neutral-800 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">CVR-nummer:</span>
                    <span className="font-bold text-[#DFBF77]">{COMPANY_INFO.cvr}</span>
                  </div>
                  <div className="flex items-start justify-between gap-4 pt-1 border-t border-neutral-800">
                    <span className="text-neutral-400">Åbningstider:</span>
                    <span className="font-medium text-right text-neutral-200">{COMPANY_INFO.openingHours}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
